import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuth as useSdkAuth } from '@next-social-platform/chatbot_sso/react';
import type { Auth } from '@next-social-platform/chatbot_sso';
import { useNavigate } from 'react-router-dom';
import { setApiAccessToken, setAuthSdk } from '../api';
import { setSsoAccessToken } from '../api/sso';
import { consumeDeepLinkState } from '../auth/oidcConfig';
import { dataColors } from '../shared/design/dataColors';

/**
 * Thin domain wrapper around the SDK's `useAuth()` hook
 * (`@next-social-platform/chatbot_sso/react`, ≥3.1.1). The SDK owns:
 *   - Auth instance lifecycle (`new Auth(config)` + `init()`)
 *   - Cross-tab event sync (`login` / `logout` / `token-refresh` / `token-expired`
 *     auto-update React state via `BroadcastChannel`)
 *   - User-info fetch on auth success (`fetchUserOnAuth` default)
 *   - StrictMode guard + OIDC callback safety net (forces `check-sso` when
 *     `isLoginCallback()`)
 *
 * This wrapper adds:
 *   - Three axios setters: `setApiAccessToken`, `setSsoAccessToken`, `setAuthSdk`
 *   - Role normalisation: `hasBookingAdmin()` derived from user.roles
 *   - `can()` permission helper
 *   - `userInfo()` accessor with Guest fallback
 *   - Dev debug toggle button (calls SDK's `setDebug`)
 *   - Error banner with dismiss (uses SDK's `clearError()`, new in 3.1.1)
 *   - Post-logout navigation to `/`
 *   - Legacy `useAuth()` return shape so call sites stay unchanged.
 */

/** Match backend `normalizeSsoRoleCodes` for OIDC userinfo `roles`. */
function ssoRoleCodesFromUser(u: unknown): string[] {
  if (!u || typeof u !== 'object') {
    return [];
  }
  const roles = (u as Record<string, unknown>).roles;
  if (!Array.isArray(roles)) {
    return [];
  }
  const out: string[] = [];
  for (const x of roles) {
    if (typeof x === 'string' && x.trim()) {
      out.push(x.trim());
    } else if (x && typeof x === 'object' && 'code' in x) {
      const c = (x as { code: unknown }).code;
      if (c != null && String(c).trim()) {
        out.push(String(c).trim());
      }
    }
  }
  return out;
}

interface IAuthContext {
  oauthUser: unknown;
  oauthToken: string | null;
  authenticated: boolean;
  user: unknown;
  token: string | null;
  isOAuth: boolean;
  can: (options: { type: string }) => boolean;
  userInfo: () => {
    name: string;
    email?: string;
    [key: string]: unknown;
  };
  /** SSO role `admin` (case-insensitive), from OIDC userinfo. */
  hasBookingAdmin: () => boolean;
  login: () => void | Promise<void>;
  logout: (options?: {
    useOIDCLogout?: boolean;
    postLogoutRedirectUri?: string;
    state?: string;
  }) => void;
  loading: boolean;
  /** Underlying SDK instance. Consumers (e.g. RequireAuth) check `isLoginCallback()`. */
  sdk: Auth | null;
  error: string | null;
}

export const AuthContext = createContext<IAuthContext | undefined>(undefined);

type AuthProviderProps = {
  children: ReactNode;
  /**
   * Kept for backward compatibility — the SDK provider's `initOptions` is
   * decided once at the app root (see `auth/oidcConfig.ts`), so this prop
   * no longer affects init mode. Left in the signature to avoid breaking
   * the `<AuthProvider oidcInitWhenUnauthenticated="login-required">` line
   * in App.tsx during the migration; will be removed in a follow-up.
   */
  oidcInitWhenUnauthenticated?: 'check-sso' | 'login-required';
};

export const AuthProvider = ({
  children,
  oidcInitWhenUnauthenticated: _oidcInitWhenUnauthenticated,
}: AuthProviderProps): JSX.Element => {
  const navigate = useNavigate();

  const sdk = useSdkAuth();
  const {
    authenticated,
    primed,
    user,
    userLoading,
    error: sdkError,
    auth,
    getAccessToken,
    login: sdkLogin,
    logout: sdkLogout,
    clearError,
  } = sdk;

  const [debugAuth, setDebugAuth] = useState(false);

  /**
   * Post-login URL restore for the chatbot.mn → booking-admin handoff.
   *
   * Why not use the SDK's `consumePostLoginRedirect()`:
   * The SDK auto-runs `handlePostLoginRedirect()` in
   * `handleAuthorizationCode` BEFORE emitting the `'login'` event
   * (auth.ts:699 vs 702). That handler reads the saved URL, compares
   * only the path component (`savedPath !== currentPath`, auth.ts:794),
   * and CLEARS the entry when paths match — even though the saved URL
   * has different query/hash. By the time our `authenticated`-watching
   * effect fires, there's nothing left to consume.
   *
   * Workaround: capture the original `search + hash` in
   * `buildOidcConfig()` (which runs at module load, before the SDK
   * touches anything) under our own sessionStorage key. Restore it
   * here once authenticated.
   *
   * Runs once per session. `restoredRef` guards re-fires on token
   * refresh re-renders.
   */
  const restoredRef = useRef(false);
  useEffect(() => {
    if (!authenticated || restoredRef.current) return;
    restoredRef.current = true;
    const saved = consumeDeepLinkState();
    if (!saved) return;
    const current =
      window.location.pathname +
      window.location.search +
      window.location.hash;
    const target = saved.path + saved.search + saved.hash;
    if (target === current) return;
    navigate(target, { replace: true });
  }, [authenticated, navigate]);

  // Apply SDK debug toggle whenever the dev flag flips. Doesn't affect
  // hot-path logic; just controls console verbosity in the SDK.
  if (auth && typeof (auth as { setDebug?: (v: boolean) => void }).setDebug === 'function') {
    (auth as { setDebug: (v: boolean) => void }).setDebug(debugAuth);
  }

  // Pull current token into a plain value so the axios setters have
  // something stable to push. The SDK provider re-renders this component
  // whenever auth state changes (init, login, logout, cross-tab refresh).
  const token = authenticated ? getAccessToken() : null;

  // Axios glue, SYNCHRONOUS during render. Using `useEffect` here loses
  // the race against children's effects: React fires effects bottom-up,
  // so a child's data-fetch effect runs BEFORE our token-sync effect
  // and 401s the first call after silent SSO. Sync render-time mutation
  // is idempotent and pushes the token into the module cache before any
  // child renders. The SDK's event emitter triggers re-renders for
  // cross-tab `token-refresh` / `logout`, so this runs again whenever
  // needed — including the previously-uncovered "tab A refreshed silently,
  // tab B's axios kept sending stale bearer" bug class.
  setApiAccessToken(token);
  setSsoAccessToken(token);
  setAuthSdk(auth ?? null);

  const login = useCallback(async () => {
    if (!auth) {
      console.error('OAuth SDK not initialized');
      return;
    }
    await sdkLogin();
  }, [auth, sdkLogin]);

  const logout = useCallback(
    (options?: {
      useOIDCLogout?: boolean;
      postLogoutRedirectUri?: string;
      state?: string;
    }) => {
      if (options?.useOIDCLogout) {
        // Full IdP logout: SDK navigates to /api/oidc/logout?client_id=...
        // and tears down the page. Local axios cleanup matters until then
        // so any in-flight request fires with no bearer.
        setApiAccessToken(null);
        setSsoAccessToken(null);
        setAuthSdk(null);
        sdkLogout({
          useOIDCLogout: true,
          postLogoutRedirectUri:
            options.postLogoutRedirectUri || window.location.origin,
          state: options.state,
        });
        return;
      }

      sdkLogout();
      // Belt + braces: clear axios immediately. SDK emits 'logout' which
      // triggers a re-render that does the same — but the navigate() below
      // doesn't tear down the React tree (SPA routing), so we want the
      // bearer gone synchronously.
      setApiAccessToken(null);
      setSsoAccessToken(null);
      setAuthSdk(null);
      navigate('/', { replace: true });
    },
    [navigate, sdkLogout],
  );

  const can = useCallback(
    (options: { type: string }) => {
      switch (options.type) {
        case 'admin':
        case 'admin-features':
        case 'menu':
        case 'any':
        case 'view':
          return authenticated;
        default:
          return false;
      }
    },
    [authenticated],
  );

  const activeUser = useMemo(
    () => (authenticated ? user : null),
    [authenticated, user],
  );

  const userInfo = useCallback(() => {
    const currentUser = activeUser as Record<string, unknown> | null;
    if (!currentUser) {
      return {
        name: 'Guest',
        email: undefined,
      };
    }

    return {
      name:
        (currentUser.name as string) ||
        (currentUser.username as string) ||
        'User',
      email: currentUser.email as string | undefined,
      ...currentUser,
    };
  }, [activeUser]);

  const hasBookingAdmin = useCallback(() => {
    return ssoRoleCodesFromUser(user).some(
      (c) => c.toLowerCase() === 'admin',
    );
  }, [user]);

  // SDK exposes `error: Error | null`; legacy API was `error: string | null`.
  // Normalize at the boundary so call sites that string-render it stay clean.
  const errorMessage = sdkError ? sdkError.message : null;

  const pack = useMemo<IAuthContext>(
    () => ({
      oauthUser: user,
      oauthToken: token,
      authenticated,
      user: activeUser,
      token,
      isOAuth: authenticated,
      can,
      userInfo,
      hasBookingAdmin,
      login,
      logout,
      // `loading=true` until BOTH SDK init AND user-info are resolved when
      // the operator is authenticated. Without the second condition, role
      // guards (e.g. RequireBookingAdminRoute) fire before `user.roles` is
      // populated and synchronously redirect away from admin routes on
      // first load — same race class as BusinessContext's "validate
      // selection against empty list" bug fixed 2026-06-15.
      loading: !primed || (authenticated && !user && userLoading),
      sdk: auth,
      error: errorMessage,
    }),
    [
      user,
      token,
      authenticated,
      activeUser,
      can,
      userInfo,
      hasBookingAdmin,
      login,
      logout,
      primed,
      userLoading,
      auth,
      errorMessage,
    ],
  );

  return (
    <AuthContext.Provider value={pack}>
      {process.env.NODE_ENV === 'development' && (
        <button
          type="button"
          onClick={() => setDebugAuth((d) => !d)}
          style={{
            position: 'fixed',
            bottom: 8,
            right: 8,
            zIndex: 99999,
            fontSize: 11,
            opacity: 0.7,
          }}
          title="Toggle OAuth SDK debug"
        >
          OAuth debug {debugAuth ? 'on' : 'off'}
        </button>
      )}
      {errorMessage && (
        <div
          role="alert"
          style={{
            margin: '0 0 8px',
            padding: '8px 12px',
            background: dataColors().dangerBox.bg,
            border: `1px solid ${dataColors().dangerBox.border}`,
            color: dataColors().dangerBox.fg,
            borderRadius: 6,
            fontSize: 14,
          }}
        >
          <strong>Authentication error:</strong> {errorMessage}{' '}
          <button
            type="button"
            onClick={() => clearError()}
            style={{ marginLeft: 8 }}
          >
            Dismiss
          </button>
        </div>
      )}
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): IAuthContext => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
